import { useAtomValue } from "jotai";
import type { NextPageContext, PageProps } from "next";
import { useEffect, useMemo } from "react";
import { graphql, usePaginationFragment, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";
import { scmpPlus } from "scmp-sdk";

import { data as appData } from "shared/data";
import { parseQueryString } from "shared/lib/utils";

import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { AppFooter } from "scmp-app/components/app-footer";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { AiArticleWidget } from "scmp-app/components/section/ai-article-widget";
import { SubscriptionWidget } from "scmp-app/components/subscription-widget";
import { TopicCollectionAds } from "scmp-app/components/topic-collection/topic-collection-ads";
import { TopicCollectionDescription } from "scmp-app/components/topic-collection/topic-collection-description";
import { TopicCollectionRestContents } from "scmp-app/components/topic-collection/topic-collection-rest-contents";
import { TopicCollectionSeo } from "scmp-app/components/topic-collection/topic-collection-seo";
import { TopicCollectionTopContents } from "scmp-app/components/topic-collection/topic-collection-top-contents";
import { TopicHeroContents } from "scmp-app/components/topic/topic-hero-contents";
import { trackingAtom } from "scmp-app/components/tracking";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { topicCollectionPageQuery } from "scmp-app/queries/__generated__/topicCollectionPageQuery.graphql";
import type { topicCollectionPageQueue$key } from "scmp-app/queries/__generated__/topicCollectionPageQueue.graphql";

import { parseList } from "./helpers";
import {
  Container,
  ContentContainer,
  ContentWrapper,
  HeroContents,
  RestContentWrapper,
  RHSModule1,
  RHSModule2,
  StyledLoading,
  StyledTopicCollectionHeader,
  SubscriptionMessage,
  TopContents1,
  TopContents2,
  TopContents3,
  TopContents4,
  TopContents5,
  TopContents6,
  TopContents7,
} from "./styles";

type TopStoresTypes = typeof TopContents1;

const topContentsComponentMap: Record<string, TopStoresTypes> = {
  TopContents1,
  TopContents2,
  TopContents3,
  TopContents4,
  TopContents5,
  TopContents6,
  TopContents7,
};

const getMaxAdsNumber = (edgesLength: number) => {
  const adsNumbers = [26, 18, 15];
  const showAdsNumbers = [4, 3, 2];
  for (const [index, totalAdsNumber] of adsNumbers.entries()) {
    if (edgesLength >= totalAdsNumber) {
      return showAdsNumbers[index];
    }
  }
  return 1;
};

const query = graphql`
  query topicCollectionPageQuery(
    $after: String
    $collectionRelatedTopicsQueueName: String!
    $collectionSmartQueueName: String!
    $entityId: String!
    $first: Int
  ) {
    collection(filter: { entityId: $entityId }) {
      entityId
      entityUuid
      urlAlias
      advertZone(version: 2)
      ...topicCollectionSeoCollection
      ...topicCollectionHeaderCollection
      ...topicCollectionAdsCollection
      ...hooksUseDynamicMetaTags
      ...topicCollectionTopContentsCollection
      ...topicCollectionRestContentsCollection
      ...topicCollectionDescriptionTopic
    }
    collectionRelatedTopicsQueue: queue(filter: { name: $collectionRelatedTopicsQueueName }) {
      items(first: 10) {
        ...topicCollectionHeaderRelatedLinksConnection
      }
    }
    collectionSmartQueue: queue(filter: { name: $collectionSmartQueueName }) {
      ...topicCollectionPageQueue @arguments(after: $after, first: $first)
      items(after: $after, first: $first) @connection(key: "topicCollectionPage__items") {
        edges {
          node {
            ... on Content {
              entityUuid
              ...homeSecondaryContentItemContent
              ...sectionTopLeadPrimaryContentItemContent
              ...topStoriesItemContent
            }
          }
        }
      }
    }
  }
`;

const TopicCollectionPage: RelayComponent<unknown, topicCollectionPageQuery> = ({
  preloadedQuery,
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);

  const {
    data: contentList,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<topicCollectionPageQuery, topicCollectionPageQueue$key>(
    graphql`
      fragment topicCollectionPageQueue on Queue
      @argumentDefinitions(after: { type: "String" }, first: { type: "Int", defaultValue: 28 })
      @refetchable(queryName: "topicCollectionPagePaginationQuery") {
        items(after: $after, first: $first) @connection(key: "topicCollectionPage__items") {
          edges {
            node {
              ... on Content {
                entityUuid
                ...homeSecondaryContentItemContent
                ...sectionTopLeadPrimaryContentItemContent
                ...topStoriesItemContent
              }
            }
          }
        }
      }
    `,
    data.collectionSmartQueue,
  );

  const isShowFooter = useMemo(
    () =>
      // this is to prevent eslint throw warning relay/unused-fields
      !contentList?.items?.edges || !hasNextPage,
    [contentList?.items?.edges, hasNextPage],
  );

  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    const { entityId, entityUuid, urlAlias } = data.collection;
    trackPageView?.({
      contentType: "TopicCollection",
      entityId,
      entityUuid,
      page: "TopicCollection",
      path: urlAlias,
    });
  }, [data.collection, trackPageView]);

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => loadNext(11),
  });

  const {
    contentItemLength,
    heroContentsGroup,
    isShowSubscriptionMessage,
    restContentsComponents,
    topContentsComponents,
  } = parseList({
    contentList,
    hasNextPage: false,
  });

  const collectionSmartQueueEdgeNodes =
    data.collectionSmartQueue?.items?.edges.map(({ node }) => node) ?? [];

  return (
    <>
      <TopicCollectionSeo reference={data.collection} />
      <Container>
        <ContentContainer>
          {data.collectionRelatedTopicsQueue?.items && (
            <StyledTopicCollectionHeader
              topicCollectionHeaderRelatedLinksConnectionReference={
                data.collectionRelatedTopicsQueue?.items
              }
              topicCollectionReference={data.collection}
            />
          )}
          <ContentWrapper>
            <HeroContents>
              {heroContentsGroup && <TopicHeroContents heroContentsGroup={heroContentsGroup} />}
            </HeroContents>
            {collectionSmartQueueEdgeNodes &&
              topContentsComponents?.map((topContents, index) => {
                const ComponentType = topContentsComponentMap[topContents.type];
                return (
                  <ComponentType key={topContents.type + index}>
                    <TopicCollectionTopContents
                      index={index}
                      reference={data.collection}
                      topContents={topContents}
                    />
                  </ComponentType>
                );
              })}
            {isShowSubscriptionMessage && (
              <SubscriptionMessage>
                <SubscriptionWidget />
              </SubscriptionMessage>
            )}
            <TopicCollectionAds
              maxAdsNumber={getMaxAdsNumber(contentItemLength)}
              reference={data.collection}
            />
            <RHSModule1>
              <TopicCollectionDescription reference={data.collection} />
            </RHSModule1>
            <RHSModule2>
              <AiArticleWidget aiEngineDatatype={AiEngineDataType.HomeRecommendedForYou} />
            </RHSModule2>
          </ContentWrapper>
          {restContentsComponents && (
            <RestContentWrapper>
              {collectionSmartQueueEdgeNodes &&
                restContentsComponents.map((restContents, index) => (
                  <TopicCollectionRestContents
                    index={index}
                    key={`rest-${index}`}
                    reference={data.collection}
                    restContents={restContents}
                  />
                ))}
            </RestContentWrapper>
          )}
          {hasNextPage && (
            <StyledLoading ref={element => infiniteScrollTriggerReference(element)} />
          )}
        </ContentContainer>
        {isShowFooter && <AppFooter />}
      </Container>
    </>
  );
};

TopicCollectionPage.displayName = "TopicCollectionPage";

const getPageProps = (context: NextPageContext) => {
  const advertZone = parseQueryString(context.query.advertZone) ?? "";
  const entityId = parseQueryString(context.query.entityId) ?? "";
  const pageProps: PageProps = {
    appBarConfiguration: {
      adSlotVariants: {
        large: {
          height: 250,
          padding: 20,
        },
        small: {
          height: 100,
          padding: 10,
        },
      },
      hasDesktopAd: true,
      hasMobileAd: true,
      topBannerAdSlot: {
        desktop: {
          adUnit: "d_banner1",
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
          targeting: {
            paid: TargetingPaidType.NotArticle,
            sctid: entityId,
          },
          zone: advertZone,
        },
        mobile: {
          adUnit: "m_banner3",
          targeting: {
            paid: TargetingPaidType.NotArticle,
            sctid: entityId,
          },
          zone: advertZone,
        },
      },
      variant: "scmp/slim",
    },
    contentConfiguration: {
      responsivePadding: {
        desktopUp: 12,
        tabletUp: 0,
      },
    },
    pageConfiguration: {
      responsivePadding: {
        tabletUp: 24,
      },
    },
  };

  return pageProps;
};

export default withRelay(TopicCollectionPage, query, {
  clientSideProps: context => ({
    ...getPageProps(context),
  }),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: async context => {
    const pageProps = Promise.resolve(getPageProps(context));
    return {
      ...(await pageProps),
    };
  },
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";
    return {
      applicationId: appData.application.scmp.entityUuid,
      collectionRelatedTopicsQueueName: `collection_related_topics_${entityId}`,
      collectionSmartQueueName: `collection_smart_queue_${entityId}`,
      entityId,
      first: 28,
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
    };
  },
});
